import type { HTMLText, Text } from 'pixi.js';
import { TextStyle } from 'pixi.js';
import cloneDeep from 'lodash/cloneDeep';
import { addLanguageChangedListener, getActiveLanguage, removeLanguageChangedListener } from '@softgames/localization';
import type { TextModel } from './types/models';
import type { LayoutText } from './LayoutText';
import type { LayoutHTMLText } from './LayoutHTMLText';
import { getLocalizedString } from '../../../app/localization/getLocalizedString';
import { mapBlendMode } from './mapBlendMode';
import { fixTextStyleV8 } from './fixTextStyleV8';

export const copyTextProps = (text: LayoutText | LayoutHTMLText, model: TextModel): void => {
  text.x = model.x;
  text.y = model.y;
  text.anchor.x = model.anchor.x;
  text.anchor.y = model.anchor.y;
  text.scale.x = model.scale.x;
  text.scale.y = model.scale.y;
  text.angle = model.angle;
  text.alpha = model.alpha;
  text.visible = model.visible;
  text.blendMode = mapBlendMode(model.blendMode);
  text.interactive = model.interactive;
  text.eventMode = model.buttonMode ? 'static' : 'auto';
  text.cursor = model.buttonMode ? 'pointer' : 'default';

  const style = cloneDeep(model.style);
  fixTextStyleV8(style);

  text.style = new TextStyle(style);

  if (typeof text.style.stroke === 'object') {
    (text.style.stroke as { join: string }).join = 'round';
  }

  if (model.localize) {
    applyLocalizations(text, model);
  } else {
    text.text = model.text;
  }

  // enforce maxWidth / maxHeight
  if (model.maxWidth > 0 || model.maxHeight > 0) {
    const maxFontSize: number | string = model.maxFontSize || (model.style.fontSize ?? 0);
    text.setTextSizeRestrictions({
      maxWidth: model.maxWidth,
      maxHeight: model.maxHeight,
      maxFontSize: typeof maxFontSize === 'string' ? parseInt(maxFontSize, 10) : maxFontSize,
    });
  }
};

export function applyLocalizations(text: Text | HTMLText, model: TextModel): void {
  const updateText = (): void => {
    const embeddedTranslations =
      (model.data as { translations?: Record<string, string | Record<string, string>> })?.translations ?? null;
    if (embeddedTranslations) {
      const phraseTranslations = (embeddedTranslations[model.text] ?? embeddedTranslations) as Record<string, string>;
      text.text = phraseTranslations[getActiveLanguage()] ?? text.text;
    } else {
      text.text = getLocalizedString(model.text);
    }
  };

  updateText();
  addLanguageChangedListener(updateText);

  text.once('destroyed', () => {
    removeLanguageChangedListener(updateText);
  });
}
